/**
 * Purpose: Pure helpers that turn an already-loaded category list into a label, a display
 * priority, a Zod validator, or a generated slug. Deliberately split out of
 * `lib/product-categories.ts` (the cached DB loader) so these are safe to import from Client
 * Components — the loader module pulls in `@lib/db-prisma` and `next/cache`, which cannot run
 * in a browser bundle; this module touches neither. Replaces the old compile-time
 * `PRODUCT_CATEGORY_VALUES` tuple and `getCategoryPriority()` (admin-product-categories
 * design.md Decision 3).
 * Inputs: A previously-loaded `ProductCategory[]` (from `getProductCategories()`, passed down as
 * a prop) plus a slug/locale/label per call — never a database read of their own.
 * Outputs: `categoryLabel`, `categorySlugs`, `categoryPriority`, `categorySlugSchema`, and
 * `slugifyCategoryLabel`.
 */
import { z } from 'zod'
import type { ProductCategory } from '@/generated/prisma/client'

/** The locale-appropriate label, or the raw slug if the list has no row for it (spec: a stored
 * product can reference a slug whose category row is gone — degrade instead of failing to render). */
export function categoryLabel(categories: ProductCategory[], slug: string, locale: string): string {
  const category = categories.find((c) => c.slug === slug)
  if (!category) return slug
  return locale === 'it' ? category.labelIt : category.labelEnUs
}

/** Slugs in display order, for building `<select>` options and the Zod validator below. */
export function categorySlugs(categories: ProductCategory[]): string[] {
  return categories.map((c) => c.slug)
}

/** Lower index = higher display priority. Replaces the old `getCategoryPriority`; an unknown
 * slug sorts last (99) instead of breaking the list order. */
export function categoryPriority(categories: ProductCategory[], slug: string): number {
  const index = categories.findIndex((c) => c.slug === slug)
  return index === -1 ? 99 : index
}

/**
 * Validates and normalises a category slug against the currently-loaded list. Every entry point
 * that writes `products.products.category` builds this fresh from `getProductCategories()`
 * rather than importing a fixed enum, so a category an admin just added is immediately valid.
 */
export function categorySlugSchema(categories: ProductCategory[]) {
  const slugs = categorySlugs(categories)
  // z.enum needs at least one literal; an empty table (never true after the seed migration)
  // falls back to a slug nothing can match, so validation fails closed rather than throwing.
  const values = (slugs.length > 0 ? slugs : ['__no_categories__']) as [string, ...string[]]
  return z
    .string()
    .transform((s) => s.toLowerCase().trim())
    .pipe(z.enum(values, { error: `Category must be one of: ${slugs.join(', ')}` }))
}

/**
 * Derives a category's slug from its en-US label: strip accents, lowercase, collapse every run
 * of non `[a-z0-9]` characters to one hyphen, trim leading/trailing hyphens, and cap at 50 chars
 * (design.md Decision 1a). Pure and client-safe, so the add form can preview it as the admin
 * types; the server re-derives it independently and never trusts a client-sent slug.
 */
export function slugifyCategoryLabel(label: string): string {
  const withoutAccents = label.normalize('NFKD').replace(/[̀-ͯ]/g, '')
  const collapsed = withoutAccents
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return collapsed.slice(0, 50).replace(/^-+|-+$/g, '')
}
